import { collection, doc, getDoc, getDocs, getFirestore, limit, query, where } from 'firebase/firestore';
import { isValidPhotoURL } from './userPhoto';

export const USER_TYPES = {
  ADMIN: { label: 'Admin', color: '#e91e63' },
  MEMBER: { label: 'Member', color: '#0bb5df' },
  ARTIST: { label: 'Artist', color: '#ff6b35' },
  VENUE_OWNER: { label: 'Venue Owner', color: '#4caf50' },
  VENUE_EMPLOYEE: { label: 'Venue Employee', color: '#795548' },
  PROMOTER: { label: 'Promoter', color: '#9c27b0' },
  BOOKING_AGENT: { label: 'Booking Agent', color: '#ff9800' },
  TOUR_MANAGER: { label: 'Tour Manager', color: '#2196f3' },
  SOUND_ENGINEER: { label: 'Sound Engineer', color: '#607d8b' },
  LIGHTING_TECHNICIAN: { label: 'Lighting Tech', color: '#ffeb3b' },
  FAN: { label: 'Fan', color: '#e91e63' },
  BAND_MEMBER: { label: 'Band Member', color: '#f1c40f' },
  MUSIC_INDUSTRY: { label: 'Music Industry', color: '#1abc9c' },
};

export const INVITE_STATUS_META = {
  pending: { label: 'Pending', color: '#f1c40f', icon: 'time-outline' },
  accepted: { label: 'Accepted', color: '#2ecc71', icon: 'checkmark-circle-outline' },
  declined: { label: 'Declined', color: '#e74c3c', icon: 'close-circle-outline' },
  revoked: { label: 'Revoked', color: '#95a5a6', icon: 'ban-outline' },
};

export function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

/** Firebase Auth UIDs are opaque alphanumeric strings — never show them as display names. */
export function isLikelyFirebaseUid(value) {
  const s = String(value || '').trim();
  return s.length >= 20 && s.length <= 128 && /^[a-zA-Z0-9]+$/.test(s);
}

function firstHumanLabel(...candidates) {
  for (const raw of candidates) {
    const value = String(raw || '').trim();
    if (!value || isLikelyFirebaseUid(value)) continue;
    return value;
  }
  return '';
}

export function memberDisplayName(profile, fallback = 'Member') {
  const first = String(profile?.firstName || '').trim();
  const last = String(profile?.lastName || '').trim();
  const full = `${first} ${last}`.trim();
  const email = normalizeEmail(profile?.email || profile?.inviteeEmail);
  const fromFields = firstHumanLabel(
    full,
    profile?.username,
    profile?.name,
    profile?.inviteeName,
    email,
    fallback
  );
  if (fromFields) return fromFields;
  if (email) {
    const local = email.split('@')[0];
    return local || email;
  }
  return String(fallback || 'Member').trim() || 'Member';
}

/** Preferred label for UI cards/chips (never a raw UID). */
export function resolveMemberLabel(member, fallback = 'Tour member') {
  const label = memberDisplayName(member, member?.name || member?.email || fallback);
  if (!isLikelyFirebaseUid(label)) return label;
  const email = normalizeEmail(member?.email);
  if (email) {
    const local = email.split('@')[0];
    return local || email;
  }
  return fallback;
}

/** Stable id for rider member blocks — prefer email so re-inserts match. */
export function tourPartyMemberBlockId(member) {
  const email = normalizeEmail(member?.email);
  if (email) return email;
  const id = String(member?.id || '').trim();
  if (id) return id;
  return resolveMemberLabel(member, 'member').toLowerCase().replace(/\s+/g, '_');
}

export function resolveUserTypeMeta(userType) {
  const key = String(userType || 'member').toUpperCase().replace(/-/g, '_');
  return USER_TYPES[key] || USER_TYPES.MEMBER;
}

export function resolveMemberPhotoURL(profile, memberKey) {
  if (isValidPhotoURL(profile?.photoURL)) return profile.photoURL.trim();
  if (isValidPhotoURL(profile?.inviteePhotoURL)) return profile.inviteePhotoURL.trim();
  return '';
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {{ ownerId?: string, invites?: Array<Record<string, unknown>> }} params
 * @returns {Promise<Map<string, Record<string, unknown>>>}
 */
export async function fetchProfilesForParty(db, { ownerId, invites = [] }) {
  const profileById = new Map();
  const pendingEmails = new Set();

  const trackUser = (userDoc) => {
    if (!userDoc?.id) return;
    profileById.set(userDoc.id, userDoc);
    const email = normalizeEmail(userDoc.email);
    if (email) profileById.set(`email:${email}`, userDoc);
  };

  const ids = new Set();
  if (ownerId) ids.add(ownerId);
  invites.forEach((row) => {
    if (row?.inviteeId) ids.add(String(row.inviteeId));
    const email = normalizeEmail(row?.inviteeEmail);
    if (email) pendingEmails.add(email);
  });

  await Promise.all(
    [...ids].map(async (id) => {
      try {
        const snap = await getDoc(doc(db, 'users', id));
        if (snap.exists()) trackUser({ id: snap.id, ...snap.data() });
      } catch (err) {
        console.warn('fetchProfilesForParty getDoc', id, err);
      }
    })
  );

  await Promise.all(
    [...pendingEmails].map(async (email) => {
      if (profileById.has(`email:${email}`)) return;
      try {
        const snap = await getDocs(
          query(collection(db, 'users'), where('email', '==', email), limit(1))
        );
        if (!snap.empty) {
          const d = snap.docs[0];
          trackUser({ id: d.id, ...d.data() });
        }
      } catch (err) {
        console.warn('fetchProfilesForParty email lookup', email, err);
      }
    })
  );

  return profileById;
}

function resolveProfileForInvite(invite, profileById) {
  const inviteeId = String(invite?.inviteeId || '').trim();
  const email = normalizeEmail(invite?.inviteeEmail);
  if (inviteeId && profileById.has(inviteeId)) return profileById.get(inviteeId);
  if (email && profileById.has(`email:${email}`)) return profileById.get(`email:${email}`);
  return null;
}

export function buildOwnerMemberRecord({ ownerId, ownerName, ownerEmail, ownerPhotoURL, profile }) {
  const merged = profile || {};
  const userType = merged.userType || 'member';
  const typeMeta = resolveUserTypeMeta(userType);
  return {
    id: ownerId,
    inviteId: null,
    name: resolveMemberLabel(
      { ...merged, email: merged.email || ownerEmail },
      memberDisplayName(merged, ownerName || 'Tour owner')
    ),
    email: normalizeEmail(merged.email || ownerEmail),
    username: String(merged.username || '').trim(),
    photoURL: resolveMemberPhotoURL(merged, ownerId) || resolveMemberPhotoURL({ photoURL: ownerPhotoURL }, ownerId),
    userType,
    userTypeLabel: typeMeta.label,
    userTypeColor: typeMeta.color,
    role: 'Tour owner',
    status: 'accepted',
    onPlatform: true,
    interests: Array.isArray(merged.interests) ? merged.interests : [],
  };
}

/**
 * @param {Record<string, unknown>} invite
 * @param {Map<string, Record<string, unknown>>} profileById
 */
export function buildInviteMemberRecord(invite, profileById) {
  const profile = resolveProfileForInvite(invite, profileById);
  const status = String(invite?.status || 'pending').toLowerCase();
  const userType = profile?.userType || invite?.inviteeUserType || '';
  const typeMeta = resolveUserTypeMeta(userType || 'member');
  const name =
    memberDisplayName(profile, String(invite?.inviteeName || '').trim() || invite?.inviteeEmail || 'Invitee');

  return {
    id: String(profile?.id || invite?.inviteeId || invite?.id || invite?.inviteeEmail || name),
    inviteId: invite?.id || null,
    name: resolveMemberLabel(
      {
        firstName: profile?.firstName,
        lastName: profile?.lastName,
        username: profile?.username,
        name: profile?.name,
        inviteeName: invite?.inviteeName,
        email: profile?.email || invite?.inviteeEmail,
      },
      name
    ),
    email: normalizeEmail(profile?.email || invite?.inviteeEmail),
    username: String(profile?.username || '').trim(),
    photoURL:
      resolveMemberPhotoURL(profile, profile?.id || invite?.inviteeEmail) ||
      resolveMemberPhotoURL({ photoURL: invite?.inviteePhotoURL }, invite?.inviteeEmail),
    userType: userType || '',
    userTypeLabel: userType ? typeMeta.label : '',
    userTypeColor: typeMeta.color,
    role: status === 'accepted' ? 'Tour party' : 'Invited',
    status,
    onPlatform: Boolean(profile?.id || invite?.inviteeId),
    interests: Array.isArray(profile?.interests) ? profile.interests : [],
  };
}

/**
 * @param {Array<Record<string, unknown>>} members
 */
export function dedupeTourPartyMembers(members) {
  const seen = new Set();
  return members.filter((member) => {
    const key = normalizeEmail(member.email) || String(member.id || member.name).toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export async function loadEnrichedTourPartyMembers({
  ownerId,
  ownerName,
  ownerEmail,
  ownerPhotoURL,
  invites = [],
}) {
  const db = getFirestore();
  const profileById = await fetchProfilesForParty(db, { ownerId, invites });
  const ownerProfile = ownerId ? profileById.get(ownerId) : null;
  const rows = [];

  if (ownerId) {
    rows.push(
      buildOwnerMemberRecord({ ownerId, ownerName, ownerEmail, ownerPhotoURL, profile: ownerProfile })
    );
  }

  invites
    .filter((row) => !['revoked', 'cancelled', 'declined'].includes(String(row?.status || '').toLowerCase()))
    .forEach((row) => {
      rows.push(buildInviteMemberRecord(row, profileById));
    });

  return dedupeTourPartyMembers(rows);
}
